import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { I18nProvider } from 'next-localization';

import {
  AuthReadyProvider,
  BasketProvider,
  FeaturesContext,
  SiteSettingsContext,
  TrackingProvider,
} from '@gn-audio/jss-components';
import DatadogRum, { DatadogRumConfig } from '@gn-audio/jss-components/components/DatadogRum';
import {
  configureAuthorization,
  FormatSettings,
  getCurrencyFormats,
  getLocaleWithRegion,
  SiteContext,
} from '@gn-audio/jss-core';

import { FormatContext } from 'common/contexts/FormatContext';
import { ProductGroupProvider } from 'common/contexts/ProductGroupContext';
import { JabraPageProps } from 'common/types/jabraPageProps';
import { bootstrapClientComponents, initializeHttpClients } from 'common/utils/bootstrapAppUtils';
import { CoveoSearchInterfaceProvider } from 'composables/Search/providers/CoveoSearchInterfaceProvider';
import Bootstrap from 'src/Bootstrap';

import ErrorPage from './_error';

import 'assets/app.css';
import '../../styles/globals.css';

function App({ Component, pageProps }: AppProps<JabraPageProps>): React.JSX.Element {
  const [formatSettings, setFormatSettings] = useState<FormatSettings>();
  const [authReady, setAuthReady] = useState<boolean>(false);
  const authBootstrapRef = useRef(false);
  const router = useRouter();
  // todo: disables next prefetch mechanism. remove after ending coexistence period(after the migration of all routes)
  useMemo(() => {
    router.prefetch = async () => {};
  }, [router]);

  const {
    dictionary,
    envConfig,
    locale,
    error: serverError,
    siteSettings,
    features,
    siteContext: rawSiteContext,
    ...rest
  } = pageProps;

  const siteContext = rawSiteContext ?? SiteContext.Jabra;

  const {
    coveoOrganizationId,
    coveoAccessToken,
    coveoContentRecommendationAccessToken,
    coveoBaseUrl,
    coveoPipeline,
    coveoSearchHub,
    coveoRecommendationsPipeline,
    coveoRecommendationsHub,
    coveoUserAnalyticsAccessToken,
    coveoUserAnalyticsEndpoint,
    datadogRumEnabled,
    datadogRumApplicationId,
    datadogRumClientToken,
    datadogRumSite,
    datadogRumService,
    datadogRumEnv,
    datadogRumVersion,
    datadogRumSessionSampleRate,
    datadogRumSessionReplaySampleRate,
  } = envConfig || {};

  const language = getLocaleWithRegion(locale);

  const datadogRumConfig: DatadogRumConfig = {
    isEnabled: datadogRumEnabled,
    applicationId: datadogRumApplicationId,
    clientToken: datadogRumClientToken,
    site: datadogRumSite,
    serviceName: datadogRumService,
    env: datadogRumEnv,
    version: datadogRumVersion,
    sessionSampleRate: datadogRumSessionSampleRate,
    sessionReplaySampleRate: datadogRumSessionReplaySampleRate,
  };

  initializeHttpClients(envConfig, siteContext);

  try {
    if (typeof window !== 'undefined' && !authBootstrapRef.current && siteSettings?.authorization) {
      configureAuthorization(siteSettings, siteContext, language);
      authBootstrapRef.current = true;
      setAuthReady(true);
    }
  } catch (error) {
    console.error('Error configuring authorization:', error);
  }

  const bootstrapApp = useCallback(async () => {
    if (typeof window !== 'undefined') {
      try {
        if (!envConfig) throw new Error('No environment configuration found');

        if (!siteSettings) throw new Error('Site settings are not defined');

        await bootstrapClientComponents(siteSettings, features);
        const formatSettings = await getCurrencyFormats(siteSettings, language);
        setFormatSettings(formatSettings);
      } catch (error) {
        console.error('Error bootstrapping client-side components:', error);
      }
    }
  }, [envConfig, language, features, siteSettings]);

  useEffect(() => {
    bootstrapApp();
  }, [bootstrapApp]);

  if (serverError) {
    console.error(serverError);
    return <ErrorPage />;
  }

  return (
    <>
      <DatadogRum config={datadogRumConfig} defaultServiceName="njord-support-frontend" />
      <Bootstrap {...pageProps} />
      {/*
        // Use the next-localization (w/ rosetta) library to provide our translation dictionary to the app.
        // Note Next.js does not (currently) provide anything for translation, only i18n routing.
        // If your app is not multilingual, next-localization and references to it can be removed.
      */}
      <AuthReadyProvider authReady={authReady}>
        <TrackingProvider>
          <FormatContext.Provider value={formatSettings}>
            <SiteSettingsContext.Provider value={siteSettings || {}}>
              <FeaturesContext.Provider value={features}>
                <I18nProvider lngDict={dictionary} locale={pageProps.locale}>
                  <BasketProvider>
                    <CoveoSearchInterfaceProvider
                      organizationId={coveoOrganizationId}
                      accessToken={coveoAccessToken}
                      baseUrl={coveoBaseUrl}
                      pipeline={coveoPipeline}
                      searchHub={coveoSearchHub}
                      recommendationsPipeline={coveoRecommendationsPipeline}
                      recommendationsHub={coveoRecommendationsHub}
                      recommendationAccessToken={coveoContentRecommendationAccessToken}
                      analyticsAccessToken={coveoUserAnalyticsAccessToken}
                      analyticsEndpoint={coveoUserAnalyticsEndpoint}
                    >
                      <ProductGroupProvider>
                        <Component {...rest} />
                      </ProductGroupProvider>
                    </CoveoSearchInterfaceProvider>
                  </BasketProvider>
                </I18nProvider>
              </FeaturesContext.Provider>
            </SiteSettingsContext.Provider>
          </FormatContext.Provider>
        </TrackingProvider>
      </AuthReadyProvider>
    </>
  );
}

export default App;
